
import BaseOption from "%/dynamic-component-editor/classes/base-option";
import { inspectorTools } from "%/dynamic-component-editor/inspector/inspector-tool-map";
import ButtonType from "%/dynamic-component-editor/options/button-type";
import ButtonSize from "%/dynamic-component-editor/options/button-size";
import BackgroundColor from "%/dynamic-component-editor/options/background-color";
import FontColor from "%/dynamic-component-editor/options/font-color";
import ButtonBorderColor from "%/dynamic-component-editor/options/button-border-color";
import ButtonBorderWidth from "%/dynamic-component-editor/options/button-border-width";
import Icon from "%/dynamic-component-editor/options/icon";
import IconPosition from "%/dynamic-component-editor/options/icon-position";
import IconColor from "%/dynamic-component-editor/options/icon-color";
import IconGap from "%/dynamic-component-editor/options/icon-gap";
import {
  INHERIT_TOKEN,
  isUnset,
  STATE_KEYS,
} from "%/dynamic-components/render-engine/helpers/subscription-action-button";

export class SubscriptionButtonText extends BaseOption {
  static userInterfaceComponent = {
    label: "Text",
    component: inspectorTools.SIMPLE_INPUT,
    useVerticalOrientation: true,
  };

  static defaultValue = "";
}

const GENERIC = {
  variant: ButtonType,
  size: ButtonSize,
  text: SubscriptionButtonText,
  fontColor: FontColor,
  backgroundColor: BackgroundColor,
  borderColor: ButtonBorderColor,
  borderWidth: ButtonBorderWidth,
  icon: Icon,
  iconPosition: IconPosition,
  iconColor: IconColor,
  gap: IconGap,
};

const CLASS_SUFFIX = {
  variant: "ButtonVariant",
  size: "ButtonSize",
  text: "ButtonText",
  fontColor: "ButtonFontColor",
  backgroundColor: "ButtonBackgroundColor",
  borderColor: "ButtonBorderColor",
  borderWidth: "ButtonBorderWidth",
  icon: "ButtonIcon",
  iconPosition: "ButtonIconPosition",
  iconColor: "ButtonIconColor",
  gap: "ButtonIconGap",
};

const STATE_PREFIX = {
  [STATE_KEYS.SUBSCRIBE]: "Subscribe",
  [STATE_KEYS.SELECTED]: "Selected",
  [STATE_KEYS.RESUBSCRIBE]: "Resubscribe",
};

const COLOR_FAMILIES = new Set(["backgroundColor", "borderColor", "borderWidth", "iconColor", "fontColor"]);

function named(cls, name) {
  Object.defineProperty(cls, "name", { value: name, configurable: true });
  cls.userInterfaceComponent = Object.getPrototypeOf(cls).userInterfaceComponent;
  return cls;
}

function makeInheritColor(Super, name) {
  class InheritColor extends Super {
    static defaultValue = { value: INHERIT_TOKEN, source: "inherit" };
    static convertConfigValueToCompiledValue(value, themeId, themeMode, optionValues) {
      if (isUnset(value)) {
        return null;
      }
      return Super.convertConfigValueToCompiledValue(value, themeId, themeMode, optionValues);
    }
  }
  return named(InheritColor, name);
}

function makeOption(family, Super, name, { forceUnsetEnum = false } = {}) {
  if (COLOR_FAMILIES.has(family)) {
    return makeInheritColor(Super, name);
  }
  const def = family === "gap" ? "auto" : family === "icon" || forceUnsetEnum ? "" : Super.defaultValue;
  class ValueOption extends Super {
    static defaultValue = def;
  }
  return named(ValueOption, name);
}

export const BASE_OPTION_CLASSES = {};
for (const [family, Super] of Object.entries(GENERIC)) {
  BASE_OPTION_CLASSES[family] = COLOR_FAMILIES.has(family) || family === "icon"
    ? makeOption(family, Super, `SubscriptionButton${CLASS_SUFFIX[family]}`)
    : Super;
}

export const DELTA_OPTION_CLASSES = {};
for (const state of Object.values(STATE_KEYS)) {
  DELTA_OPTION_CLASSES[state] = {};
  for (const [family, Super] of Object.entries(GENERIC)) {
    const name = `${STATE_PREFIX[state]}${CLASS_SUFFIX[family]}`;
    DELTA_OPTION_CLASSES[state][family] = makeOption(family, Super, name, { forceUnsetEnum: true });
  }
}

export function baseOptionClasses() {
  return Object.values(BASE_OPTION_CLASSES);
}

export function deltaOptionsForState(state) {
  return Object.values(DELTA_OPTION_CLASSES[state]);
}
